import React, { useEffect, useId, useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import * as Sentry from '@sentry/react';
import { ModalFooterAlignment, Modal, ModalBody, ModalFooter, ModalTitle } from 'design-system/src/components/Modal';
import { Typography } from 'design-system/src/components/Typography/Typography';
import { RadioButton, RadioGroup, RadioLabel, RadioOption } from 'design-system/src/components/Radio/Radio';
import { Input } from 'design-system/src/components/Input/Input';
import Button from 'design-system/src/components/Button/Button';
import { StandardErrorToast } from 'design-system/src/components/Toast/Toast';
import { toast } from 'react-hot-toast';
import { createTrackingProps } from 'shared-utils/analytics';
import { client } from '../../api';

export type SkipEhrConnectionReason = 'need_more_information' | 'practice_not_ready' | 'self_hosted_server' | 'other';

type SkipEhrConnectionModalProps = {
  onClose: () => void;
  onSuccess?: () => void;
};

type ReasonOption = {
  label: string;
  value: SkipEhrConnectionReason;
};

const reasonOptions: ReasonOption[] = [
  { label: 'I need more information about the benefits', value: 'need_more_information' },
  { label: "My practice isn't ready to connect yet", value: 'practice_not_ready' },
  { label: 'We are unable to connect because we use a self-hosted server', value: 'self_hosted_server' },
  { label: 'Other', value: 'other' },
];

const SkipEhrConnectionModal = ({ onClose, onSuccess }: SkipEhrConnectionModalProps) => {
  const [selectedReason, setSelectedReason] = useState<SkipEhrConnectionReason | null>(null);
  const [otherReason, setOtherReason] = useState('');
  const radioGroupLabelId = useId();
  const descriptionIdBase = useId();
  const otherReasonInputRef = useRef<HTMLInputElement>(null);

  const showOtherInput = selectedReason === 'other';
  const isFormValid = selectedReason !== null && (selectedReason !== 'other' || !!otherReason.trim());

  const descriptionBoxClassName = 'rounded-md p-2 bg-grayscale-50';

  useEffect(() => {
    if (showOtherInput) {
      otherReasonInputRef.current?.focus();
    }
  }, [showOtherInput]);

  const dismissEhrUpsell = useMutation({
    mutationFn: async (payload: { reason: SkipEhrConnectionReason; specify_other_reason?: string }) => {
      const response = await client.post('/ehr-upsell/dismiss', payload);
      return response.data;
    },
    onSuccess: () => {
      // NOTE: we're intentionally skipping the success toast; the upsell being removed is sufficient
      // Wrap onSuccess in try/finally to ensure onClose always runs, even if onSuccess throws.
      // This prevents the modal from remaining open if the success callback encounters an error.
      try {
        onSuccess?.();
      } finally {
        onClose();
      }
    },
    onError: (error) => {
      toast.custom(() => <StandardErrorToast />);
      console.error('Failed to dismiss EHR upsell', error);
      Sentry.captureException(error);
    },
  });

  const handleSubmit = () => {
    if (dismissEhrUpsell.isPending || !isFormValid || !selectedReason) {
      return;
    }

    dismissEhrUpsell.mutate({
      reason: selectedReason,
      ...(selectedReason === 'other' && otherReason ? { specify_other_reason: otherReason.trim() } : {}),
    });
  };

  return (
    <Modal onClose={onClose}>
      <ModalTitle>Skip EHR Connection</ModalTitle>
      <ModalBody className="flex flex-col gap-6">
        <Typography id={radioGroupLabelId}>Help us understand why you're choosing to skip this step:</Typography>

        <RadioGroup aria-labelledby={radioGroupLabelId} className="flex flex-col gap-3">
          {reasonOptions.map((option) => {
            const isSelected = selectedReason === option.value;
            const descriptionId =
              option.value === 'need_more_information'
                ? `${descriptionIdBase}-more-information`
                : option.value === 'practice_not_ready'
                  ? `${descriptionIdBase}-practice-not-ready`
                  : undefined;

            return (
              <div key={option.value} className="flex flex-col gap-3">
                <RadioOption className="items-start" htmlFor={option.value}>
                  <RadioButton
                    type="radio"
                    id={option.value}
                    name="skipEhrConnectionReason"
                    value={option.value}
                    checked={isSelected}
                    aria-describedby={isSelected ? descriptionId : undefined}
                    onChange={() => setSelectedReason(option.value)}
                  />
                  <RadioLabel as="span">{option.label}</RadioLabel>
                </RadioOption>

                {isSelected && descriptionId && option.value === 'need_more_information' && (
                  <div className={descriptionBoxClassName}>
                    <Typography id={descriptionId} styledAs="bodyExtraSmallDMSans">
                      Contact{' '}
                      <Button
                        variant="link"
                        className={`
                        !text-purple-700 underline
                        hover:!text-purple-800
                      `}
                        href="mailto:provider-support@withtandem.com"
                        textStyledAs="bodyExtraSmallDMSans"
                        textWeight={600}
                      >
                        provider-support@withtandem.com
                      </Button>{' '}
                      to learn more about how EHR integration can speed up your PA process.
                    </Typography>
                  </div>
                )}

                {isSelected && descriptionId && option.value === 'practice_not_ready' && (
                  <div className={descriptionBoxClassName}>
                    <Typography id={descriptionId} styledAs="bodyExtraSmallDMSans">
                      You can set up EHR integration later in{' '}
                      <Typography renderedAs="span" weight={600} styledAs="bodyExtraSmallDMSans">
                        Settings &gt; EHR Integration.
                      </Typography>
                    </Typography>
                  </div>
                )}

                {isSelected && option.value === 'other' && (
                  <div className="ml-10">
                    <Input
                      ref={otherReasonInputRef}
                      label="Please specify the reason for skipping"
                      hideLabel
                      placeholder="Please specify"
                      value={otherReason}
                      onChange={(event: React.ChangeEvent<HTMLInputElement>) => setOtherReason(event.target.value)}
                    />
                  </div>
                )}
              </div>
            );
          })}
        </RadioGroup>
      </ModalBody>
      <ModalFooter align={ModalFooterAlignment.Right}>
        <Button
          variant="tertiary"
          onClick={onClose}
          size="sm"
          disabled={dismissEhrUpsell.isPending}
          className="!w-[100px]"
          {...createTrackingProps({ elementName: 'Skip EHR Connection Modal: Back Button', unmask: true })}
        >
          Back
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={!isFormValid || dismissEhrUpsell.isPending}
          isLoading={dismissEhrUpsell.isPending}
          size="sm"
          className="!w-[100px]"
          {...createTrackingProps({ elementName: 'Skip EHR Connection Modal: Submit Button', unmask: true })}
        >
          Submit
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default SkipEhrConnectionModal;
